import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, Sparkles } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BackButton, Breadcrumbs, ExternalLink, useEscape } from "../lib/nav";

const SUGGEST = ["Something dark for a 2am drive", "Warm-up set for a house party", "Best ambient for focus", "Show me acid techno"];

export default function Guide() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [sid] = useState(() => localStorage.getItem("rav3_guide_sid") || (() => { const s = "guide_" + Math.random().toString(36).slice(2, 12); localStorage.setItem("rav3_guide_sid", s); return s; })());
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (!loading && !user) nav("/login", { state: { from: "/guide" } }); }, [loading, user, nav]);
  useEffect(() => { if (user) api.get(`/ai/guide/history/${sid}`).then((r) => setMsgs(r.data)).catch(() => {}); }, [user, sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { id: Date.now() + "u", role: "user", content: message }, { id: Date.now() + "a", role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/guide/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid, message }) });
      if (!res.ok) throw new Error("bad");
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta || ev.error) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + (ev.delta || ev.error) }; return c; });
        }
      }
    } catch { setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: "The guide is unavailable right now." }; return c; }); }
    setBusy(false);
  };

  const render = (t) => t.split(/(\/recording\/[a-z0-9_]+|\*\*[^*]+\*\*)/g).map((s, i) =>
    s.startsWith("/recording/") ? <Link key={i} to={s} className="text-[#E8552A] underline">open set →</Link> : s.startsWith("**") ? <strong key={i}>{s.slice(2, -2)}</strong> : s);

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10 fade-up">
      <p className="mono-label text-[#E8552A] flex items-center gap-2"><Sparkles size={14} /> RAV3 GUIDE · POWERED BY CLAUDE</p>
      <div className="flex items-center gap-4 flex-wrap mb-4"><BackButton fallback="/" /><Breadcrumbs items={[{ label: "Live", to: "/" }, { label: "Guide" }]} /></div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase mt-2">Tell me the mood.</h1>
      <p className="text-[#A1A1AA] mt-3">Your in-house selector recommends sets from the archive by mood, genre or moment.</p>

      <div data-testid="guide-messages" className="card-copper rounded-2xl mt-8 p-5 min-h-[340px] max-h-[55vh] overflow-y-auto space-y-4">
        {msgs.length === 0 && (
          <div className="grid sm:grid-cols-2 gap-2">
            {SUGGEST.map((s) => <button key={s} data-testid="guide-suggestion" onClick={() => send(s)} className="text-left text-sm border border-[#27272A] rounded-lg px-4 py-3 hover:border-[#E8552A] text-[#D4D4D8]">{s}</button>)}
          </div>
        )}
        {msgs.map((m) => (
          <div key={m.id} data-testid={`guide-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#E8552A] text-black" : "bg-[#0A0A0C] border border-[#27272A] text-[#F4F4F5]"}`}>{m.content ? render(m.content) : <span className="opacity-60">…</span>}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 mt-4">
        <input data-testid="guide-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. deep, hypnotic techno for late night" className="flex-1 bg-[#0A0A0C] border border-[#27272A] rounded-lg px-4 py-3 text-sm outline-none focus:border-[#E8552A]" />
        <button data-testid="guide-send" disabled={busy || !input.trim()} className="copper-btn rounded-lg px-5 disabled:opacity-50"><Send size={16} /></button>
      </form>
    </div>
  );
}
